import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import CommonIcons from "@base/assets/icons";
import CommonStyles from "@components/CommonStyles";
import { EAppointmentStatus } from "@base/enums";
import useToggle from "@base/hooks/useToggle";
import { IAppointment, IAttachment } from "@base/modules/appointment/appointment.interface";
import appointmentService from "@base/modules/appointment/appointmentService";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";
import DetailCard from "./DetailCard";

/** Files can only be removed while the appointment is still pending. */
const EDITABLE_STATUSES: string[] = [EAppointmentStatus.PENDING];

const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg"];

const getFileName = (attachment: IAttachment) => {
  const objectName = attachment.object_name ?? "";

  return objectName.split("/").pop() || "Unnamed";
};

const isImage = (attachment: IAttachment) => IMAGE_EXTENSIONS.some((extension) => getFileName(attachment).toLowerCase().endsWith(extension));

interface IProps {
  appointment: IAppointment;
  onChanged: () => void;
}

export default function AppointmentAttachments({ appointment, onChanged }: IProps) {
  //! state
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { open: openConfirm, shouldRender: shouldRenderConfirm, toggle: toggleConfirm } = useToggle();

  const [pending, setPending] = useState<IAttachment>();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  //! function
  const canEdit = EDITABLE_STATUSES.includes(String(appointment.status));

  const onDelete = async () => {
    if (!pending?.object_name || !canEdit) {
      return;
    }

    try {
      setIsDeleting(true);
      await appointmentService.deleteAttachment(pending.object_name, appointment.key);
      dispatch(setAlertMessage({ type: "success", message: t("Notification"), description: t("Successfully") + "!" }));
      toggleConfirm();
      onChanged();
    } catch (error) {
      dispatch(setAlertMessage({ type: "error", message: t("Notification"), description: t("Failed") + "!" }));
    } finally {
      setIsDeleting(false);
    }
  };

  //! render
  if (!appointment.attachments?.length) {
    return null;
  }

  return (
    <DetailCard title={t("packageBooking.attachments")}>
      <div className="flex flex-col gap-2">
        {appointment.attachments.map((attachment) => (
          <div key={attachment.object_name} className="flex items-center gap-3 bg-common-background rounded-lg p-3">
            {isImage(attachment) ? (
              <CommonStyles.ImageUI className="rounded-lg object-cover" width={40} height={40} src={attachment.link} />
            ) : (
              <CommonIcons.IconTextSnippet />
            )}

            <a
              href={attachment.link}
              target="_blank"
              rel="noreferrer"
              className="grow text-common-maintext text-sm font-normal font-['Inter'] line-clamp-1 hover:underline"
            >
              {getFileName(attachment)}
            </a>

            {canEdit ? (
              <CommonStyles.ButtonUI
                size="small"
                type="link"
                danger
                onClick={() => {
                  setPending(attachment);
                  toggleConfirm();
                }}
              >
                {t("Delete")}
              </CommonStyles.ButtonUI>
            ) : null}
          </div>
        ))}
      </div>

      {shouldRenderConfirm && (
        <CommonStyles.ModalConfirmationUI
          open={openConfirm}
          toggle={toggleConfirm}
          title={t("appointmentDetail.cancelConfirmTitle")}
          content={t("appointmentDetail.deleteAttachmentConfirm")}
          okButtonProps={{ loading: isDeleting }}
          onConfirm={onDelete}
        />
      )}
    </DetailCard>
  );
}
